import React, { useEffect } from "react";

import { useState } from "react";
import {
  Bookmark,
  Heart,
  MessageCircle,
  Share2,
  Tag,
  Eye,
  Calendar,
  Clock,
  ChevronLeft,
  FileText,
  Loader2
} from "lucide-react";
import { useParams } from "react-router-dom";
import {
  getGallyoSentimentArticle,
  getMarketPulseArticle
} from "../services/article";

// Mock data for the article

function Button({
  children,
  variant = "default",
  className = "",
  onClick,
  size = "default",
  ...props
}) {
  const baseClasses =
    "rounded-md font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2";

  const sizeClasses = {
    sm: "px-3 py-1.5 text-sm",
    default: "px-4 py-2",
    lg: "px-6 py-3 text-lg"
  };

  const variants = {
    default: "bg-gray-200 text-gray-800 hover:bg-gray-300 focus:ring-gray-500",
    primary:
      "bg-purple-600 text-white hover:bg-purple-700 focus:ring-purple-500 shadow-sm hover:shadow-md",
    outline:
      "border border-gray-300 text-gray-700 hover:bg-gray-50 focus:ring-gray-500 bg-white",
    ghost: "text-gray-700 hover:bg-gray-100 focus:ring-gray-500"
  };

  return (
    <button
      className={`${baseClasses} ${sizeClasses[size]} ${
        variants[variant]
      } ${className}`}
      onClick={onClick}
      {...props}
    >
      {children}
    </button>
  );
}

function ArticleDetailPage() {
  const formatDate = dateString => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric"
    });
  };

  const formatCreatedAt = dateString => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric"
    });
  };

  const [article, setArticle] = useState(null);
  const { id, articleSlug, category } = useParams();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(
    () => {
      async function getArticleDetail() {
        try {
          setIsLoading(true);
          if (category === "market-pulse") {
            const response = await getMarketPulseArticle(id); // represents the id of article ethat u want
            setArticle(response);
          } else {
            const response = await getGallyoSentimentArticle(id); // represents the id of article ethat u want
            setArticle(response);
          }
        } catch (error) {
          console.error("Error while fetching article: ", error);
        } finally {
          setIsLoading(false);
        }
      }
      if (!id) {
        return;
      }
      getArticleDetail();
    },
    [id]
  );
  return (
    <div className="min-h-screen bg-gray-50 font-afacad">
      {/* Article Content */}
      {isLoading && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-50 flex items-center justify-center z-50">
          <Loader2 className="animate-spin" width={64} height={64} />
        </div>
      )}

      {article && (
        <div className="w-full mx-auto px-2 sm:px-2 lg:px-2 py-2 bg-white">
          {/* Article Header */}
          <div className="bg-white rounded-xl shadow-sm border w-full overflow-hidden mb-8">
            {/* Cover Image */}
            {article.cover_image_url && (
              <div className="w-full h-64 sm:h-80 lg:h-96 relative">
                <img
                  src={article.cover_image_url}
                  alt={article.article_title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
              </div>
            )}

            {/* Article Meta */}
            <div className="p-6 sm:p-8">
              {/* Category and Tags */}
              <div className="flex flex-wrap items-center gap-4 mb-6">
                {article.category && (
                  <span className="px-3 py-1.5 rounded-full text-sm font-medium bg-amber-100 text-amber-800 flex items-center gap-2">
                    {article.category.name}
                  </span>
                )}
              </div>

              {/* Title and Subtitle */}
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-fraunces mb-4 text-gray-900 leading-tight">
                {article.article_title}
              </h1>

              {article.article_subtitle && (
                <>
                  <p className="text-lg sm:text-xl text-gray-600 leading-relaxed">
                    {article.article_subtitle}
                  </p>
                  <div className="flex items-center gap-2 text-sm text-gray-500 mt-2">
                    <Calendar className="w-4 h-4" />
                    <span>Published on {formatDate(article.created_at)}</span>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Article Content */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 sm:p-8 mb-8">
            <div
              className="prose prose-sm sm:prose-lg max-w-none prose-headings:text-gray-900 prose-p:text-gray-700 prose-strong:text-gray-900 prose-code:text-purple-600 prose-code:bg-purple-50 prose-pre:bg-gray-100 prose-pre:border prose-pre:border-gray-200"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default ArticleDetailPage;
